import {
  Box,
  Checkbox,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { ArrowDownward, ArrowUpward } from "@material-ui/icons";
import ListSkeleton from "../../Components/Skeleton/ListSkeleton";
import { ReactNode } from "react";

const useStyles = makeStyles((theme) => ({
  table: {
    width: "98%",
    maxHeight: "70vh",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "60vh",
    },
  },
  hint: {
    position: "relative",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    marginTop: "auto",
    marginBottom: "auto",
    marginLeft: "auto",
    marginRight: "auto",
    fontSize: "32px",
    textAlign: "center",
    color: "rgba(0,0,0,.6)",
    [theme.breakpoints.down("sm")]: {
      position: "unset",
    },
  },
  stickyColumnHeader: {
    position: "sticky",
    left: 0,
    zIndex: theme.zIndex.appBar + 2,
  },
}));

export default function SharedTable<T extends { clientName: string }>({
  data,
  isLoading,
  columns,
  sortedProperty,
  onSort,
  ascending,
  error = false,
}: {
  data: T[];
  isLoading: boolean;
  columns: TableColumn[];
  sortedProperty: string;
  onSort: (column: TableColumn) => void;
  ascending: boolean;
  error: boolean;
}) {
  const tableClasses = useStyles();
  return (
    <TableContainer
      className={tableClasses.table}
      component={Paper}
      style={{ padding: data.length !== 0 ? undefined : "20px 0" }}
    >
      {error ? (
        <div className={tableClasses.hint}>There was an error fetching the data</div>
      ) : isLoading ? (
        <ListSkeleton numRows={10} />
      ) : data.length !== 0 ? (
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => {
                return (
                  <TableCell
                    className={column.propName === "clientName" ? tableClasses.stickyColumnHeader : ""}
                    align="left"
                    style={{ fontWeight: "bold" }}
                    key={`columnheader-${index}`}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        style={{
                          width: "max-content",
                          maxWidth: "22ch",
                          lineHeight: "1.25em",
                        }}
                      >
                        {column.label}
                      </Box>
                      <IconButton onClick={() => onSort(column)}>
                        {sortedProperty === column.propName && ascending === true ? (
                          <ArrowUpward />
                        ) : (
                          <ArrowDownward />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((elem, index) => (
              <UsageTableRow columns={columns} element={elem} key={elem.clientName} index={index} />
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className={tableClasses.hint}>No Results</div>
      )}
    </TableContainer>
  );
}

const useCellStyles = makeStyles((theme) => ({
  rowCell: {
    background: theme.palette.primary.blue,
    textDecoration: "none",
  },
  rowCellOdd: {
    background: "white",
    textDecoration: "none",
  },
  stickyColumn: {
    position: "sticky",
    left: 0,
    zIndex: theme.zIndex.appBar + 1,
    background: "#D2EEFA",
  },
  stickyColumnOdd: {
    position: "sticky",
    left: 0,
    zIndex: theme.zIndex.appBar + 1,
    background: "white",
  },
}));

function UsageTableRow<T extends object>({
  columns,
  element,
  index,
}: {
  columns: TableColumn[];
  element: T;
  index: number;
}) {
  const classes = useCellStyles();
  return (
    <TableRow
      className={index % 2 ? classes.rowCell : classes.rowCellOdd}
      key={index}
      data-test="base-client-table-row"
    >
      {columns.map((column, elementIndex) => {
        const rowValue = (element as Record<string, ReactNode>)[column.propName];
        if (column.propName === "clientName") {
          const className = index % 2 ? classes.stickyColumn : classes.stickyColumnOdd;
          return (
            <TableCell align="left" key={elementIndex} className={className}>
              {rowValue}
            </TableCell>
          );
        }
        if (column.propName === "siteUrl") {
          return (
            <TableCell align="left" key={elementIndex}>
              <a href={rowValue as string} target="_blank" rel="noreferrer">
                {rowValue}
              </a>
            </TableCell>
          );
        }
        return (
          <TableCell align="center" key={elementIndex}>
            <Checkbox checked={rowValue as boolean} disabled />
          </TableCell>
        );
      })}
    </TableRow>
  );
}
